import { Box, Icon, TextButton } from '@zillow/constellation-10';
import { IconChevronRightFilled, IconNoteOutline } from '@zillow/constellation-icons';
import { useContext } from 'react';
import { useNotesBar_GetInboxPageDataQuery } from '../../types/GraphqlTypes';
import { ModalType } from '../../types/ModalType';
import {
  emitNotesButtonClickEvent,
  NOTES_BUTTON_CLICK_EVENT_TYPE_ID,
} from '../../constants/clickstreamEvents/notesModal';
import useModal from '../../hooks/useModal';
import { InboxPageContext } from '../../contexts/InboxPageContextProvider';
import StatusLabel from '../StatusLabel';
import { useAuth } from '@zg-rentals/auth-comet';

const NotesBar = (): JSX.Element | null => {
  const { listingAlias, conversationId, clientTimeZone } = useContext(InboxPageContext);
  const { setActiveModal } = useModal();
  const { user } = useAuth();
  const { data: conversationData } = useNotesBar_GetInboxPageDataQuery({
    variables: {
      listingAlias: listingAlias || '',
      conversationId: conversationId || '',
      clientTimeZone,
    },
    fetchPolicy: 'cache-only',
  });

  if (!conversationData) {
    return null;
  }
  const { statusLabel, note, applicationAlreadySent } = conversationData?.ZRM_getConversationById ?? {};
  const tourScheduledTimestampMs = conversationData?.ZRM_getConversationById.attributes?.tourScheduledTimestampMs;
  const applicationMarkedSent = conversationData?.ZRM_getConversationById.attributes?.applicationMarkedSent;
  const { note: noteText } = note;

  const showStatusLabel = statusLabel && !!statusLabel.text;
  const hasNotesData = !!noteText || applicationAlreadySent || applicationMarkedSent || !!tourScheduledTimestampMs;
  const textDisplay = hasNotesData ? 'See notes' : 'Take a note';

  const handleNotesBarClick = (): void => {
    emitNotesButtonClickEvent(user, { listingAlias });

    setActiveModal(ModalType.NotesModal);
  };

  return (
    <TextButton
      data-eventid={NOTES_BUTTON_CLICK_EVENT_TYPE_ID}
      aria-haspopup="dialog"
      data-testid="notes-bar"
      onClick={handleNotesBarClick}
      tone="neutral"
      textStyle="body-sm-bold"
      icon={
        <Icon css={{ color: 'brand' }} size="md">
          <IconNoteOutline />
        </Icon>
      }
      css={{
        display: 'flex',
        justifyContent: 'flex-start',
        padding: 'tight',
        backgroundColor: 'backgroundLight',
        '& span': {
          width: '100%',
        },
      }}
    >
      <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {textDisplay}

        {showStatusLabel && (
          <Box
            css={{
              display: 'flex',
              alignItems: 'center',
              gap: 'tighter',
            }}
          >
            <StatusLabel text={statusLabel?.text || ''} linkId={statusLabel?.linkId || ''} />
            <Icon css={{ color: 'icon.muted' }}>
              <IconChevronRightFilled />
            </Icon>
          </Box>
        )}
      </Box>
    </TextButton>
  );
};

export default NotesBar;
